import React, { useEffect, useState } from 'react';
import { ALL_CLIENTS } from '../data/clients';
import { fetchClients } from '../lib/storage';
import { ClientLogo } from './ClientLogo';
import { ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ClientGridProps {
  title?: string;
  subtitle?: string;
  showFullDirectory?: boolean;
  compact?: boolean;
}

export function ClientGrid({
  title = "Enterprise Client Ecosystem & Brand Logos",
  subtitle = "High-performance web architecture, e-commerce storefronts, and brand networks engineered with our technical standards.",
  showFullDirectory = true,
  compact = false
}: ClientGridProps) {
  const [clients, setClients] = useState(ALL_CLIENTS);
  useEffect(() => {
    const load = () => fetchClients().then(setClients);
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(load);
      return () => window.cancelIdleCallback(id);
    }
    const id = globalThis.setTimeout(load, 1000);
    return () => globalThis.clearTimeout(id);
  }, []);
  return (
    <div className="space-y-8 font-sans w-full overflow-hidden">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-[#0059bb]">Client Showcase</span>
          <h2 className="text-2xl sm:text-3xl font-display font-bold text-[#0b1c30] mt-1">
            {title}
          </h2>
          <p className="text-sm text-gray-600 max-w-2xl mt-1 leading-relaxed">
            {subtitle}
          </p>
        </div>
      </div>

      {/* Visual Logos Grid - All 12 Clients */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {clients.map((client) => (
          <div key={client.id} className="min-w-0">
            <ClientLogo client={client} showDetails={!compact} />
          </div>
        ))}
      </div>

      {/* Full Client Directory List - High Density & Guaranteed Zero Overflow */}
      {showFullDirectory && (
        <div className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100 pb-4">
            <div>
              <h3 className="text-lg sm:text-xl font-display font-bold text-[#0b1c30]">Complete Client &amp; Domain Directory</h3>
              <p className="text-xs sm:text-sm text-gray-500">Live platforms, enterprise catalogs, and digital infrastructure.</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {clients.length} Enterprise Portals
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {clients.map((client) => (
              <div
                key={client.id}
                className="p-3.5 bg-gray-50/80 hover:bg-blue-50/60 rounded-xl border border-gray-200 hover:border-[#0059bb] transition-all flex items-center justify-between gap-2 min-w-0 group"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs sm:text-sm font-semibold text-[#0b1c30] group-hover:text-[#0059bb] truncate">
                    {client.name}
                  </div>
                  <div className="text-xs text-[#0059bb] font-mono truncate break-all block" title={client.domain}>
                    {client.domain}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white text-gray-600 border border-gray-200 uppercase tracking-wide">
                    {client.category.split(' ')[0]}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs sm:text-sm text-gray-500 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#0059bb]" />
              <span>All client portals engineered under strict confidentiality, performance, and WCAG accessibility standards.</span>
            </div>
            <Link to="/contact" className="text-[#0059bb] font-semibold hover:underline shrink-0">
              Inquire About Similar Architecture →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
